// TutorCourseCard — one course card on the tutor CoursesPage: header (code +
// title, mode badge, status badge), a two-column body (progress bar left,
// session-count dots right), and an action-row footer.
import { Link } from "react-router-dom";
import Badge from "../shared/Badge.js";
import type { Course, CourseStats, CourseStatus } from "@dm-lms/shared/types";

const STATUS_BADGE: Record<CourseStatus, { label: string; tone: "gray" | "blue" | "green" }> = {
  not_started: { label: "Not started", tone: "gray" },
  in_progress: { label: "In progress", tone: "blue" },
  completed:   { label: "Completed",   tone: "green" },
};

const PROGRESS_BAR_COLOR: Record<CourseStatus, string> = {
  not_started: "bg-gray-400",
  in_progress: "bg-blue-500",
  completed:   "bg-green-500",
};

interface TutorCourseCardProps {
  course: Course;
  // Undefined while GET /api/courses/:courseId/stats is in flight for this
  // course — and, importantly, also if that fetch resolves but fails for
  // just this one course (CoursesPage's Promise.all still completes; the
  // failed course simply never gets an entry in statsByCourse). Handled
  // below with a skeleton rather than a defaulted zero, so a real fetch
  // failure never gets silently displayed as "no sessions, no students".
  stats: CourseStats | undefined;
}

function TutorCourseCardSkeleton() {
  return (
    <div className="animate-pulse rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="h-2.5 w-16 rounded bg-gray-200" />
          <div className="h-4 w-48 rounded bg-gray-200" />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="h-5 w-16 rounded-full bg-gray-200" />
          <div className="h-5 w-20 rounded-full bg-gray-200" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-gray-200" />
          <div className="h-2 w-full rounded-full bg-gray-100" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-32 rounded bg-gray-200" />
          <div className="h-3 w-28 rounded bg-gray-200" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
        <div className="h-7 w-24 rounded-md bg-gray-200" />
        <div className="h-7 w-16 rounded-md bg-gray-200" />
        <div className="h-7 w-20 rounded-md bg-gray-200" />
      </div>
    </div>
  );
}

export default function TutorCourseCard({ course, stats }: TutorCourseCardProps) {
  if (!stats) return <TutorCourseCardSkeleton />;

  const { pastSessions, upcomingSessions, totalEnrolled, completedStudents, pausedStudents, courseStatus } = stats;
  const statusBadge = STATUS_BADGE[courseStatus];
  const progressPercent = totalEnrolled > 0 ? Math.round((completedStudents / totalEnrolled) * 100) : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      {/* HEADER */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-dm-slate">{course.code}</p>
          <p className="text-[15px] font-medium text-dm-navy">{course.title}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge tone="gray">{course.mode}</Badge>
          <Badge tone={statusBadge.tone}>{statusBadge.label}</Badge>
        </div>
      </div>

      {/* BODY — two columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm font-medium text-dm-navy">Student progress</span>
            {totalEnrolled > 0 && (
              <span className="text-xs text-dm-slate">
                {completedStudents} of {totalEnrolled} completed
              </span>
            )}
          </div>
          {totalEnrolled === 0 ? (
            <p className="text-sm text-dm-slate">No students enrolled</p>
          ) : (
            <>
              <div className="h-2 w-full rounded-full bg-gray-100">
                <div
                  className={`h-2 rounded-full transition-all ${PROGRESS_BAR_COLOR[courseStatus]}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              {pausedStudents > 0 && <p className="mt-1 text-[11px] text-dm-slate">· {pausedStudents} paused</p>}
            </>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-sm text-dm-navy">
            <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
            {upcomingSessions} upcoming session{upcomingSessions === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2 text-sm text-dm-navy">
            <span className="h-2 w-2 shrink-0 rounded-full bg-gray-400" />
            {pastSessions} past session{pastSessions === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* FOOTER — action row */}
      <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
        <Link
          to={`/app/tutor/sessions?courseId=${course.id}`}
          className="rounded-md bg-dm-navy px-3 py-1.5 text-xs font-medium text-white hover:bg-dm-blue"
        >
          View sessions
        </Link>
        <Link
          to={`/app/tutor/roster?courseId=${course.id}`}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-dm-navy hover:bg-gray-50"
        >
          Roster
        </Link>
        <Link
          to={`/app/tutor/attendance?courseId=${course.id}`}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-dm-navy hover:bg-gray-50"
        >
          Attendance
        </Link>
      </div>
    </div>
  );
}
